// File: frontend/src/components/HomePage.js

import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Download } from 'lucide-react';
import { useLayout } from '../context/LayoutContext';
import LivingWatercolorRiver from './LivingWatercolorRiver';
import MarketingLayout from './marketing/MarketingLayout';
import StoreBadge from './downloads/StoreBadge';
import { STORE_LINKS } from './downloads/releases';

// Ambient color-shifting background — three soft accent blobs that slowly
// drift via transform only (GPU-composited, no filter/blend). Painted by
// MarketingLayout under the page content.
function AmbientBlobs() {
  return (
    <div className="absolute top-0 left-0 right-0 bottom-0 pointer-events-none overflow-hidden">
      <div
        className="homepage-blob"
        style={{
          top: '-15%', left: '-15%', width: '70%', height: '70%',
          '--blob-color': 'var(--color-accent-blue)',
          opacity: 0.55,
          animation: 'driftA 55s ease-in-out infinite',
        }}
      />
      <div
        className="homepage-blob"
        style={{
          bottom: '-20%', right: '-15%', width: '80%', height: '80%',
          '--blob-color': 'var(--color-accent-pink)',
          opacity: 0.5,
          animation: 'driftB 70s ease-in-out infinite',
          animationDelay: '-15s',
        }}
      />
      <div
        className="homepage-blob"
        style={{
          top: '30%', left: '20%', width: '55%', height: '55%',
          '--blob-color': 'var(--color-accent-gold)',
          opacity: 0.4,
          animation: 'driftC 60s ease-in-out infinite',
          animationDelay: '-30s',
        }}
      />
    </div>
  );
}

// The two pill buttons: solid ink, and an outlined twin.
const PILL = 'group relative inline-flex items-center justify-center px-8 py-4 text-lg font-bold font-serif rounded-full overflow-hidden transition-transform hover:scale-105 hover:shadow-lg w-full sm:w-auto';
const PILL_SOLID = `${PILL} bg-[var(--color-ink-main)] text-[var(--color-paper-bg)]`;
const PILL_OUTLINE = `${PILL} border-2 border-[var(--color-ink-main)] text-[var(--color-ink-main)] bg-transparent`;

function HomePage() {
  const { config } = useLayout();

  return (
    <MarketingLayout background={<AmbientBlobs />}>
      {/* Hero Section - Split Layout */}
      <header className={`${config.spacing.container} pt-2 pb-12 md:pt-4 md:pb-16 lg:pt-6 lg:pb-20`}>
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Text Content */}
          <div className="text-center lg:text-left animate-fade-in-up">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold font-serif text-[var(--color-ink-main)] mb-8 leading-[0.9] tracking-tight">
              Your habits,<br />
              <span className="italic text-[var(--color-ink-light)] relative inline-block">
                always in sight.
                <svg className="absolute -bottom-2 left-0 w-full h-3 text-[var(--color-accent-blue)] opacity-50" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" className="animate-ink-spread-slow" />
                </svg>
              </span>
            </h1>

            <p className="text-xl text-[var(--color-ink-light)] leading-relaxed font-serif italic mb-10 max-w-xl mx-auto lg:mx-0">
              A habit tracker designed to be seen. Beautiful enough for your living room wall, simple enough for your daily life.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <Link to="/login" className={PILL_SOLID}>
                <span className="absolute top-0 left-0 right-0 bottom-0 bg-[var(--color-ink-light)] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500"></span>
                <span className="relative z-10 flex items-center gap-3">
                  Start Tracking Free <ArrowRight className="w-5 h-5" />
                </span>
              </Link>
              <Link to="/download" className={PILL_OUTLINE}>
                <span className="relative z-10 flex items-center gap-3">
                  <Download className="w-5 h-5" /> Download for your device
                </span>
              </Link>
            </div>
            <p className="text-sm font-serif text-[var(--color-ink-subtle)] italic mt-4">No credit card required</p>

            {/* Store badges under the primary CTA */}
            <div className="flex items-center gap-3 mt-6 justify-center lg:justify-start">
              <StoreBadge
                url={STORE_LINKS.iosAppStore}
                badge="/images/badges/app-store.svg"
                label="Download on the App Store"
                imgClass="h-11"
              />
              <StoreBadge
                url={STORE_LINKS.playStore}
                badge="/images/badges/google-play.png"
                label="Get it on Google Play"
                imgClass="h-[66px] -my-3"
              />
            </div>
          </div>

          {/* Hero Image / Illustration */}
          <div className="relative animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <div className="relative z-10 transform rotate-2 hover:rotate-0 transition-transform duration-700 duration-500">
              <img
                src="/images/wall.webp"
                alt="Steadily.me on a living room wall"
                className="rounded-[2rem] shadow-2xl border-4 border-[var(--color-paper-warm)] w-full max-w-md mx-auto lg:max-w-full"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Living Watercolor River */}
      <LivingWatercolorRiver />

      {/* Interactive Demo Section - Centered & Straight */}
      <section className="px-4 md:px-8 py-16 md:py-32 bg-[var(--color-paper-warm)]/30 relative overflow-x-hidden">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold font-serif text-[var(--color-ink-main)] mb-6">
            Try it right here
          </h2>
          <p className="text-[var(--color-ink-light)] max-w-2xl mx-auto text-lg">
            The first section shows your progress over the whole year at a glance. The next shows this week. Tap the circles to mark a habit as completed.
          </p>
        </div>

        {/* Demo Container — the real app running in demo mode. The card's
            background matches the Godot boot splash, so loading reads as
            blank paper until the engine paints. Always interactive: the
            demo shell blocks Godot's wheel/touchmove handlers (see
            fetch-godot-web.js), so page scrolling chains through the
            iframe while clicks land in the app. */}
        <div className="relative max-w-5xl mx-auto rounded-[2rem] shadow-xl border border-[var(--color-ink-subtle)]/10 overflow-hidden bg-[#FBF7EE] aspect-[3/4] sm:aspect-[4/3] md:aspect-[16/10]">
          <iframe
            src="/app/?demo=1"
            title="Steadily.me interactive demo"
            className="absolute inset-0 w-full h-full border-0"
          />
        </div>
      </section>

      {/* Scenarios Section - Desk & Wall */}
      <section className="px-4 md:px-8 py-24 md:py-32">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center mb-32">
            {/* Scenario 1: Desk */}
            <div className="order-2 md:order-1">
              <div className="relative group">
                <div className="absolute top-0 left-0 right-0 bottom-0 bg-[var(--color-accent-blue)] rounded-[2rem] transform rotate-3 group-hover:rotate-1 transition-transform duration-500 opacity-20"></div>
                <img
                  src="/images/kitchen.webp"
                  alt="Steadily.me on a kitchen counter"
                  className="relative z-10 rounded-[2rem] shadow-lg w-full transform transition-transform duration-500 group-hover:-translate-y-2"
                />
              </div>
            </div>
            <div className="order-1 md:order-2 text-center md:text-left">
              <h2 className="text-3xl md:text-4xl font-bold font-serif text-[var(--color-ink-main)] mb-6">
                Visible
              </h2>
              <p className="text-lg text-[var(--color-ink-light)] leading-relaxed mb-8">
                Keep your habits visible while you live your life. A gentle reminder that doesn't yell for immediate attention like a notification. It just sits there, looking beautiful, waiting for you to succeed.
              </p>
              <ul className="space-y-4 text-left inline-block">
                <li className="flex items-center gap-3 text-[var(--color-ink-main)]">
                  <CheckCircle2 className="w-5 h-5 text-[var(--color-accent-green)]" />
                  <span>Minimalist "Always On" mode</span>
                </li>
                <li className="flex items-center gap-3 text-[var(--color-ink-main)]">
                  <CheckCircle2 className="w-5 h-5 text-[var(--color-accent-green)]" />
                  <span>Works on iOS, Android, Windows, Linux, Mac, or the Web</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            {/* Scenario 2: Hall */}
            <div className="text-center md:text-left">
              <h2 className="text-3xl md:text-4xl font-bold font-serif text-[var(--color-ink-main)] mb-6">
                Peaceful
              </h2>
              <p className="text-lg text-[var(--color-ink-light)] leading-relaxed mb-8">
                Turn an old tablet or a Raspberry Pi into helpful art. Steadily.me dims at night and wakes up with you in the morning. It's part of your home, not just an app.
              </p>
              <ul className="space-y-4 text-left inline-block">
                <li className="flex items-center gap-3 text-[var(--color-ink-main)]">
                  <CheckCircle2 className="w-5 h-5 text-[var(--color-accent-green)]" />
                  <span>Auto-dimming night mode</span>
                </li>
                <li className="flex items-center gap-3 text-[var(--color-ink-main)]">
                  <CheckCircle2 className="w-5 h-5 text-[var(--color-accent-green)]" />
                  <span>Large, readable interface</span>
                </li>
              </ul>
              <p className="mt-8">
                <Link
                  to="/install"
                  className="inline-flex items-center gap-2 font-serif font-bold text-[var(--color-ink-main)] underline underline-offset-4 decoration-[var(--color-accent-gold)] hover:decoration-[var(--color-ink-main)] transition-colors"
                >
                  See how to set one up <ArrowRight className="w-4 h-4" />
                </Link>
              </p>
            </div>
            <div>
              <div className="relative group">
                <div className="absolute top-0 left-0 right-0 bottom-0 bg-[var(--color-accent-gold)] rounded-[2rem] transform -rotate-2 group-hover:rotate-0 transition-transform duration-500 opacity-20"></div>
                <img
                  src="/images/hallway.webp"
                  alt="Steadily.me on a hallway table"
                  className="relative z-10 rounded-[2rem] shadow-lg w-full transform transition-transform duration-500 group-hover:-translate-y-2"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="px-4 md:px-8 py-20 md:py-24 bg-[var(--color-surface-elevated)] border-t border-[var(--color-divider)]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-serif text-[var(--color-ink-main)] mb-4">
            Ready for an always-on display?
          </h2>
          <p className="text-lg text-[var(--color-ink-light)] leading-relaxed mb-8">
            Free on every platform. Grab the build for your device, or turn a photo frame, a Raspberry Pi or a spare PC into a display, on a wall or a desk, that boots straight into your habits.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/download" className={PILL_SOLID}>
              <span className="relative z-10 flex items-center gap-3">
                <Download className="w-5 h-5" /> Download
              </span>
            </Link>
            <Link to="/install" className={PILL_OUTLINE}>
              <span className="relative z-10 flex items-center gap-3">
                Set up a display <ArrowRight className="w-5 h-5" />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

export default HomePage;
